import { useMemo, useRef, useState } from "react"

import {
  TableOptions,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  boolean,
  fuzzyFilter,
  inDateRange,
  inNumberRange,
  multiEnum,
  singleEnum,
} from "./filterFns"
import { LE_TableInstance, LE_TableOptions } from "./types"

export const useLumenTable: <TData extends Record<string, any> = object>(
  opts: LE_TableOptions<TData>,
) => LE_TableInstance<TData> = <TData extends Record<string, any> = object>(
  opts: LE_TableOptions<TData>,
) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showFilter, setShowFilter] = useState(false)

  const globalSearchInputRef = useRef<HTMLInputElement>(null)

  const {
    data,
    columns,
    onSortingChange,
    onGlobalFilterChange,
    onColumnVisibilityChange,
    onColumnFiltersChange,
    onPaginationChange,
    onRowSelectionChange,
    onColumnPinningChange,
    state,
    initialState,
    tableId,
    debounceGlobalSearch,
    manualFiltering,
    manualPagination,
    manualSorting,
    enablePagination,
    getRowId,
    enableRowSelection,
    enableMultiRowSelection,
    renderBulkActions,
    isLoading,
    getExportRows,
    meta,
  } = opts

  const tableOptions: TableOptions<TData> & LE_TableOptions<TData> = {
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    ...(onColumnVisibilityChange ? { onColumnVisibilityChange } : {}),
    ...(onColumnFiltersChange ? { onColumnFiltersChange } : {}),
    ...(onGlobalFilterChange ? { onGlobalFilterChange } : {}),
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onPaginationChange ? { onPaginationChange } : {}),
    ...(onRowSelectionChange
      ? {
          onRowSelectionChange: (updater) =>
            onRowSelectionChange(
              typeof updater === "function"
                ? updater(state.rowSelection ?? {})
                : updater,
            ),
        }
      : {}),
    ...(onColumnPinningChange ? { onColumnPinningChange } : {}),
    defaultColumn: {
      size: undefined,
      sortUndefined: 1,
      enableHiding: true,
      header: undefined,
      filterFn: undefined,
      enableSorting: !manualSorting,
      ...(manualSorting ? { sortDescFirst: true } : {}),
    },
    filterFns: {
      boolean,
      singleEnum,
      multiEnum,
      inNumberRange,
      fuzzy: fuzzyFilter,
      inDateRange,
    },
    globalFilterFn: "fuzzy",
    state: {
      ...(state.columnVisibility ? { columnVisibility: state.columnVisibility } : {}),
      ...(state.columnFilters ? { columnFilters: state.columnFilters } : {}),
      ...(state.sorting ? { sorting: state.sorting } : {}),
      ...(state.globalFilter ? { globalFilter: state.globalFilter } : {}),
      ...(state.pagination ? { pagination: state.pagination } : {}),
      ...(state.rowSelection ? { rowSelection: state.rowSelection } : {}),
      ...(state.columnPinning ? { columnPinning: state.columnPinning } : {}),
      isFullscreen,
      showFilter,
      total: state.total,
      isLoading,
    },
    initialState,
    enableGlobalFilter: true,
    enableRowSelection,
    enableMultiRowSelection,
    manualFiltering,
    manualPagination,
    manualSorting,
    getRowId,
    meta,
  }

  const table = useReactTable(tableOptions) as LE_TableInstance<TData>

  table.setIsFullscreen = setIsFullscreen
  table.setShowFilter = setShowFilter

  const memoRandomId = useMemo(() => Math.random().toString(36).slice(2, 7), [])
  table.tableId = tableId || memoRandomId
  table.getElementId = (suffix) => `${table.tableId}-${suffix}`
  table.debounceGlobalSearch = debounceGlobalSearch
  table.enablePagination = enablePagination
  table.refs = {
    globalSearchInputRef,
  }
  table.renderBulkActions = renderBulkActions
  table.getExportRows = getExportRows

  return table
}
